import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type FetchCatalogPage<T extends { id: number }> = (args: {
  page: number;
  limit: number;
  query: string;
}) => Promise<{ items: T[]; totalPages: number }>;

function mergeUniqueAppend<T extends { id: number }>(prev: T[], incoming: T[]): T[] {
  const seen = new Set(prev.map((x) => Number(x.id)));
  const out = [...prev];
  for (const x of incoming) {
    const id = Number(x.id);
    if (Number.isNaN(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(x);
  }
  return out;
}

function mergeDisplay<T extends { id: number }>(extras: T[], pins: T[], server: T[]): T[] {
  const seen = new Set<number>();
  const out: T[] = [];
  for (const list of [extras, pins, server]) {
    for (const item of list) {
      const id = Number(item.id);
      if (Number.isNaN(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(item);
    }
  }
  return out;
}

export interface UseAsyncPaginatedCatalogParams<T extends { id: number }> {
  /** Ej. drawer abierto: si false, se limpia estado y no se pide al API. */
  isActive: boolean;
  fetchPage: FetchCatalogPage<T>;
  pageSize?: number;
  debounceMs?: number;
  /** Valores que deben mostrarse aunque no vengan en la página actual (selección, edición). */
  pinItems?: T[];
}

export interface UseAsyncPaginatedCatalogResult<T extends { id: number }> {
  displayOptions: T[];
  loading: boolean;
  onOpen: () => void;
  onInputChange: (event: unknown, newInputValue: string, reason: string) => void;
  listboxProps: {
    style: React.CSSProperties;
    onScroll: (e: React.UIEvent<HTMLUListElement>) => void;
  };
  filterOptions: (options: T[]) => T[];
  prependItems: (items: T[]) => void;
}

export function useAsyncPaginatedCatalog<T extends { id: number }>(
  params: UseAsyncPaginatedCatalogParams<T>,
): UseAsyncPaginatedCatalogResult<T> {
  const { isActive, fetchPage, pageSize = 10, debounceMs = 350, pinItems = [] } = params;

  const [serverItems, setServerItems] = useState<T[]>([]);
  const [pageLoaded, setPageLoaded] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [extraItems, setExtraItems] = useState<T[]>([]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeqRef = useRef(0);
  const pendingInputRef = useRef('');
  const loadingRef = useRef(false);
  const pageLoadedRef = useRef(0);
  const totalPagesRef = useRef(1);
  const openedRef = useRef(false);
  const fetchPageRef = useRef(fetchPage);

  fetchPageRef.current = fetchPage;

  useEffect(() => {
    loadingRef.current = loading;
    pageLoadedRef.current = pageLoaded;
    totalPagesRef.current = totalPages;
  }, [loading, pageLoaded, totalPages]);

  const resetInternal = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    requestSeqRef.current += 1;
    setServerItems([]);
    setPageLoaded(0);
    setTotalPages(1);
    setDebouncedQuery('');
    setExtraItems([]);
    setLoading(false);
    pendingInputRef.current = '';
    openedRef.current = false;
  }, []);

  useEffect(() => {
    if (!isActive) {
      resetInternal();
    }
  }, [isActive, resetInternal]);

  const runFetch = useCallback(
    async (page: number, append: boolean) => {
      const seq = ++requestSeqRef.current;
      setLoading(true);
      try {
        const { items, totalPages: tp } = await fetchPageRef.current({
          page,
          limit: pageSize,
          query: debouncedQuery,
        });
        if (seq !== requestSeqRef.current) return;
        setTotalPages(tp);
        setPageLoaded(page);
        if (append) {
          setServerItems((prev) => mergeUniqueAppend(prev, items));
        } else {
          setServerItems(items);
        }
      } finally {
        if (seq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    },
    [pageSize, debouncedQuery],
  );

  useEffect(() => {
    if (!isActive || !openedRef.current) return;
    requestSeqRef.current += 1;
    setServerItems([]);
    setPageLoaded(0);
    setTotalPages(1);
    void runFetch(1, false);
  }, [debouncedQuery, isActive, runFetch]);

  const onOpen = useCallback(() => {
    openedRef.current = true;
    void runFetch(1, false);
  }, [runFetch]);

  const scheduleQuery = useCallback(
    (raw: string) => {
      pendingInputRef.current = raw;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        setDebouncedQuery(pendingInputRef.current.trim());
      }, debounceMs);
    },
    [debounceMs],
  );

  const onInputChange = useCallback(
    (_event: unknown, newValue: string, reason: string) => {
      if (reason === 'input') {
        scheduleQuery(newValue);
      } else if (reason === 'clear') {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        setDebouncedQuery('');
      }
    },
    [scheduleQuery],
  );

  const onListboxScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      const t = e.currentTarget;
      const nearBottom = t.scrollTop + t.clientHeight >= t.scrollHeight - 40;
      if (!nearBottom || loadingRef.current) return;
      if (pageLoadedRef.current >= totalPagesRef.current) return;
      const nextPage = pageLoadedRef.current + 1;
      void runFetch(nextPage, true);
    },
    [runFetch],
  );

  const listboxProps = useMemo(
    () => ({
      style: { maxHeight: 280 } as React.CSSProperties,
      onScroll: onListboxScroll,
    }),
    [onListboxScroll],
  );

  const filterOptions = useCallback((options: T[]) => options, []);

  const prependItems = useCallback((items: T[]) => {
    setExtraItems((prev) => mergeUniqueAppend(items, prev));
  }, []);

  const displayOptions = useMemo(
    () => mergeDisplay(extraItems, serverItems, pinItems),
    [extraItems, serverItems, pinItems],
  );

  return {
    displayOptions,
    loading,
    onOpen,
    onInputChange,
    listboxProps,
    filterOptions,
    prependItems,
  };
}
